import { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  {
    num: "01",
    icon: "🌲",
    title: "Ecosystems Restoration",
    items: [
      "Management of Forests, Mangroves, Grasslands & Wetlands",
      "Human-Wildlife Conflict Management",
      "DPR, Project Formulation & Planning",
      "Project Execution & Appraisal",
      "Research, Study, Monitoring & Evaluation",
    ],
    clients: "Forest & Environment Departments, Associated Organisations",
  },
  {
    num: "02",
    icon: "🌱",
    title: "Social Forestry",
    items: [
      "Agro Forestry & Farm Forestry",
      "Tree Plantation on Public Lands",
      "Tree Felling & Transit Permissions",
      "Medicinal Plants Cultivation & Projects",
      "Research & Study",
    ],
    clients: "Farmers, Institutions, Government Departments",
  },
  {
    num: "03",
    icon: "💼",
    title: "Livelihood",
    items: [
      "Income Generating Activities Identification",
      "Identification of Viable Activities",
      "Business Plan Preparation",
      "Value Addition, Branding & Marketing",
    ],
    clients: "Self Help Groups, Entrepreneurs, NGOs",
  },
  {
    num: "04",
    icon: "🤲",
    title: "Social",
    items: [
      "Community Participation",
      "PRA Tools & Techniques",
      "Individual vs Group Behaviour",
      "Joint Forest Management",
      "Research & Study",
    ],
    clients: "Forest Department and its Officials",
  },
  {
    num: "05",
    icon: "⚖️",
    title: "Legal & Policy",
    items: [
      "Forest Laws and Policies",
      "Biodiversity Legal Framework",
      "PESA & FRA, Panchayat Laws",
      "Societies / Trusts Policy & Rules",
      "Policy Reforms & Regulations",
    ],
    clients: "Forest Department Officials",
  },
  {
    num: "06",
    icon: "✍️",
    title: "Content Writing",
    items: [
      "Documentary & Quickie Films",
      "Book Writing",
      "Professional Presentations",
      "Training Material Preparation",
    ],
    clients: "Film Makers, Training Institutes, Forest Department",
  },
];

const AREAS = [
  "Ecosystems Restoration",
  "Social Forestry",
  "Livelihood Development",
  "Social Mobilisation",
  "Legal & Policy Advisory",
  "Content Writing",
];

const initialForm = {
  name: "",
  organisation: "",
  email: "",
  phone: "",
  area: "",
  message: "",
};

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e, i) => {
          if (e.isIntersecting) {
            setTimeout(() => e.target.classList.add("visible"), i * 100);
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const nodes = (rootRef.current || document).querySelectorAll(".fade-in");
    nodes.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!form.name.trim() || !form.email.trim() || !form.area || !form.message.trim()) {
      setStatus({ type: "error", message: "Please fill in all required fields (marked with *)." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setStatus({ type: "error", message: "Please enter a valid email address." });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus({
        type: "success",
        message: "✅ Thank you! Your inquiry has been submitted. Dr. Prasad will be in touch shortly.",
      });
      setForm(initialForm);
      setTimeout(() => setStatus({ type: "", message: "" }), 7000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        type: "error",
        message: typeof detail === "string" ? detail : "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={rootRef} data-testid="home-page">
      {/* NAV */}
      <nav className="site-nav" data-testid="site-nav">
        <a href="#home" className="nav-logo" onClick={closeMenu} data-testid="nav-logo-link">
          <img
            src="/logo.png"
            alt="Dr. Jagdish Prasad logo"
            className="nav-logo-img"
            data-testid="nav-logo-img"
          />
          <span className="nav-logo-text">Dr. <span>Jagdish Prasad</span></span>
        </a>
        <ul className={`nav-links ${menuOpen ? "open" : ""}`} data-testid="nav-links">
          <li><a href="#home" onClick={closeMenu} data-testid="nav-home">Home</a></li>
          <li><a href="#about" onClick={closeMenu} data-testid="nav-about">About</a></li>
          <li><a href="#services" onClick={closeMenu} data-testid="nav-services">Services</a></li>
          <li><a href="#contact" onClick={closeMenu} data-testid="nav-contact">Contact</a></li>
        </ul>
        <div
          className="hamburger"
          onClick={() => setMenuOpen((m) => !m)}
          aria-label="Menu"
          data-testid="hamburger-btn"
        >
          <span></span><span></span><span></span>
        </div>
      </nav>

      {/* HERO */}
      <section id="home">
        <div className="hero-bg-pattern"></div>
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">🌿 Freelance Consultancy Services</div>
            <h1 className="hero-title">
              Dr. Jagdish<br />
              <span className="accent">Prasad</span>
            </h1>
            <p className="hero-subtitle">Expert | Advisor | Author | Trainer</p>
            <p className="hero-desc">
              Bringing decades of field expertise in Ecosystems Restoration, Social Forestry,
              Livelihood Development, Policy &amp; Legal Frameworks, and Content Writing — to Forest
              Departments, NGOs, Farmers, SHGs, and Institutions across India.
            </p>
            <div className="hero-btns">
              <a href="#services" className="btn-primary" data-testid="hero-explore-btn">Explore Services</a>
              <a href="#contact" className="btn-outline" data-testid="hero-contact-btn">Get in Touch</a>
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat-card">
              <div className="stat-icon">🌳</div>
              <div className="stat-label">Specialisation</div>
              <div className="stat-value">6 Core Areas</div>
              <div className="stat-desc">Ecosystems to Content Writing</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🤝</div>
              <div className="stat-label">Clients Served</div>
              <div className="stat-value">Govt. · NGOs · SHGs</div>
              <div className="stat-desc">Forest Depts, Farmers &amp; Institutions</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-label">Services Include</div>
              <div className="stat-value">DPR · M&amp;E · Training</div>
              <div className="stat-desc">End-to-end project consultancy</div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about">
        <div className="section-inner">
          <div className="section-label">Who I Am</div>
          <h2 className="section-title">About the Consultant</h2>
          <p className="section-desc">
            A seasoned professional with deep domain knowledge and hands-on field experience
            across India's forestry and environmental sectors.
          </p>
          <div className="about-grid fade-in">
            <div className="about-portrait">
              <div className="portrait-frame" data-testid="about-logo-frame">
                <img
                  src="/logo.png"
                  alt="Dr. Jagdish Prasad — Ecosystems & Livelihood Consultant"
                  className="portrait-logo"
                  data-testid="about-logo-img"
                />
              </div>
            </div>
            <div className="about-info">
              <p>
                Dr. Jagdish Prasad is a distinguished freelance consultant with extensive
                experience working with Forest &amp; Environment Departments, NGOs, Self Help
                Groups, farming communities, and policy-making bodies across India.
              </p>
              <p>
                His expertise spans the full lifecycle of environmental and social projects —
                from concept formulation and Detailed Project Reports (DPR) to execution,
                monitoring &amp; evaluation. He brings a rare combination of academic rigour,
                field practicality, and policy understanding to every engagement.
              </p>
              <p>
                Whether advising on Human-Wildlife Conflict management, guiding SHGs on
                livelihood strategies, or preparing professional training material and
                documentary scripts, Dr. Prasad delivers contextual, actionable, and impactful
                consultancy.
              </p>
              <div className="about-highlights">
                <div className="highlight-item">
                  <h4>Domain</h4>
                  <p>Ecosystems &amp; Forestry</p>
                </div>
                <div className="highlight-item">
                  <h4>Focus</h4>
                  <p>Policy, Livelihood &amp; Research</p>
                </div>
                <div className="highlight-item">
                  <h4>Mode</h4>
                  <p>Freelance / Independent</p>
                </div>
                <div className="highlight-item">
                  <h4>Reach</h4>
                  <p>Pan-India</p>
                </div>
              </div>
              <a
                href="https://www.linkedin.com/in/dr-jagdish-prasad-8850193a5/"
                target="_blank"
                rel="noopener noreferrer"
                className="linkedin-btn"
                data-testid="linkedin-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                View LinkedIn Profile
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services">
        <div className="section-inner">
          <div className="section-label">What I Offer</div>
          <h2 className="section-title">Consultancy Services</h2>
          <p className="section-desc">
            Six specialized areas of expertise tailored to the needs of government bodies,
            institutions, NGOs, and communities.
          </p>
          <div className="services-grid">
            {SERVICES.map((s) => (
              <div className="service-card fade-in" key={s.num} data-testid={`service-card-${s.num}`}>
                <div className="service-num">{s.num}</div>
                <div className="service-icon">{s.icon}</div>
                <h3 className="service-title">{s.title}</h3>
                <ul className="service-list">
                  {s.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <div className="service-clients">
                  For: <span>{s.clients}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact">
        <div className="section-inner">
          <div className="section-label">Reach Out</div>
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-desc">
            Have a project in mind or need expert guidance? Fill in the form and Dr. Prasad
            will respond promptly.
          </p>
          <div className="contact-grid fade-in">
            <div className="contact-info-block">
              <h3>Ready to Collaborate?</h3>
              <p>
                Whether you're a government department, NGO, farmer's institution, or
                entrepreneur — reach out to discuss how expert consultancy can add value to
                your work.
              </p>
              <div className="contact-areas">
                {AREAS.map((a) => (
                  <div className="area-tag" key={a}>
                    <div className="area-tag-dot"></div>
                    {a}
                  </div>
                ))}
              </div>
            </div>

            <form className="contact-form" onSubmit={onSubmit} data-testid="contact-form" noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Your Name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid="input-name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="organisation">Organisation</label>
                  <input
                    type="text"
                    id="organisation"
                    name="organisation"
                    placeholder="Your Organisation"
                    value={form.organisation}
                    onChange={onChange}
                    data-testid="input-organisation"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="input-email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="input-phone"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="area">Area of Consultancy *</label>
                <select
                  id="area"
                  name="area"
                  value={form.area}
                  onChange={onChange}
                  required
                  data-testid="input-area"
                >
                  <option value="" disabled>Select an area...</option>
                  <option value="Ecosystems Restoration">Ecosystems Restoration</option>
                  <option value="Social Forestry">Social Forestry</option>
                  <option value="Livelihood">Livelihood</option>
                  <option value="Social">Social</option>
                  <option value="Legal & Policy">Legal &amp; Policy</option>
                  <option value="Content Writing">Content Writing</option>
                  <option value="Other / Multiple Areas">Other / Multiple Areas</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="message">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Briefly describe your requirement or project..."
                  value={form.message}
                  onChange={onChange}
                  required
                  data-testid="input-message"
                />
              </div>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting}
                data-testid="submit-btn"
              >
                {submitting ? "Sending..." : "Send Inquiry →"}
              </button>
              {status.message && (
                <div
                  className={status.type === "success" ? "form-success" : "form-error"}
                  data-testid="form-status"
                >
                  {status.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="site-footer">
        <p>
          © 2025 <strong>Dr. Jagdish Prasad</strong> — Freelance Consultancy Services &nbsp;|&nbsp;
          <a
            href="https://www.linkedin.com/in/dr-jagdish-prasad-8850193a5/"
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn Profile
          </a>
        </p>
      </footer>

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/919978406168"
        className="whatsapp-float"
        title="Chat on WhatsApp"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float"
      >
        💬
      </a>
    </div>
  );
}
